/**
 * Estimate session state shared by the estimate pages:
 * form drafts, the current result and an optional comparison result.
 * Kept in sessionStorage so a refresh (or a detour to sign in / buy a pass) keeps the user's work.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const SESSION_KEY = 'rumahkira_estimate_session';

const EMPTY = { quickDraft: null, detailedDraft: null, result: null, compare: null };

const readSession = () => {
  try {
    return { ...EMPTY, ...JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}') };
  } catch {
    return EMPTY;
  }
};

const EstimateContext = createContext(null);

export const EstimateProvider = ({ children }) => {
  const [state, setState] = useState(readSession);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [state]);

  const setQuickDraft = useCallback((quickDraft) => setState((s) => ({ ...s, quickDraft })), []);
  const setDetailedDraft = useCallback((detailedDraft) => setState((s) => ({ ...s, detailedDraft })), []);
  const setResult = useCallback((result) => setState((s) => ({ ...s, result, compare: null })), []);
  const setCompare = useCallback((compare) => setState((s) => ({ ...s, compare })), []);
  const updateResult = useCallback((patch) => setState((s) => (s.result ? { ...s, result: { ...s.result, ...patch } } : s)), []);

  const value = useMemo(
    () => ({ ...state, setQuickDraft, setDetailedDraft, setResult, setCompare, updateResult }),
    [state, setQuickDraft, setDetailedDraft, setResult, setCompare, updateResult]
  );

  return <EstimateContext.Provider value={value}>{children}</EstimateContext.Provider>;
};

export const useEstimate = () => {
  const ctx = useContext(EstimateContext);
  if (!ctx) throw new Error('useEstimate must be used within an EstimateProvider');
  return ctx;
};
